import { Authorization } from "../components/form-components/AuthorizationForm";
import { AdditionalForm } from "../interfaces/additionalForm";
import { Client } from "../interfaces/client";
import { Order, SimpleOrder } from "../interfaces/order";
import { OrderForm } from "../interfaces/orderForm";
import { QuestionOption } from "../interfaces/questionOption";
import { Questions } from "../interfaces/questions";
import {
  ReferenceDetail,
  SimpleReferenceDetail,
} from "../interfaces/referenceDetaill";
import { ReferenceFormAccess } from "../interfaces/referenceFormAccesss";
import { ReferenceQuestionnaire } from "../interfaces/referenceQuestionnaire";
import { fetchWithAuth } from "./fetchWithAuth";
import { convertBlobToBase64, convertBlobUrlToBase64 } from "./helpers";
import { HttpService } from "./httpService";

const APP_URL = process.env.REACT_APP_APP_URL || "";
const DEV_APP_URL = process.env.REACT_APP_DEV_APP_URL || "";
const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";

const siteBaseUrl =
  window.location.hostname === "localhost"
    ? APP_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_APP_URL
      : DEV_APP_URL;

const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

// This will create a reference questionnaire record with base text, questions, and question options
export const createReferenceQuestionnaire = async (
  clientID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/ReferenceQuestionnaire/CreateReferenceQuestionnaire/${clientID}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
  );

  if (!response.ok) {
    throw new Error(
      "Failed to create the reference questionnaire for the new client.",
    );
  }
};

export const getReferenceQuestionnaireBase = async (
  clientID: number,
): Promise<ReferenceQuestionnaire> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/ReferenceQuestionnaire/GetReferenceQuestionnaireBase/${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch Reference Questionnaire Base.");
  }

  return await response.json();
};

export const getReferenceQuestionnaireQuestions = async (
  referenceQuestionnaireID: number,
): Promise<Questions[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/ReferenceQuestionnaire/GetQuestions/${referenceQuestionnaireID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      "Failed to fetch questions for the Reference Questionnaire.",
    );
  }

  return await response.json();
};

export const getReferenceQuestionnaireQuestionOptions = async (
  questionID: number,
): Promise<QuestionOption[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/ReferenceQuestionnaire/GetQuestionOptions/${questionID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch question options for the question.");
  }

  return await response.json();
};

export const sendReferenceQuestionnairePdf = async (
  referenceDetail: ReferenceDetail,
  order: Order,
  client: Client,
  referenceQuestionnaire: ReferenceQuestionnaire,
  signatureForm: AdditionalForm | null,
  authForm: OrderForm | null,
  logoForm: AdditionalForm | null,
  questionsWithOptions: Map<
    number,
    { questionText: string; options: QuestionOption[] }
  >,
  byMail: boolean,
  diocese: string,
  diocese2: string,
): Promise<boolean> => {
  try {
    let signedSignatureBase64 = "";
    if (signatureForm) {
      const signedSignatureUrl = await HttpService.getFileAdmin(
        "signatures",
        client.clientID.toString(),
        signatureForm?.completedFormUrl,
      );

      const response = await fetch(signedSignatureUrl);
      const blob = await response.blob();
      signedSignatureBase64 = await convertBlobToBase64(blob);
      URL.revokeObjectURL(signedSignatureUrl);
    }

    let signedAuthBase64 = "";
    if (authForm) {
      const authUrl = await HttpService.getFileAdmin(
        "reference-screening-authorizations",
        authForm.orderFormID.toString(),
        authForm.completedFormUrl,
      );

      const response = await fetch(authUrl);
      const blob = await response.blob();
      signedAuthBase64 = await convertBlobToBase64(blob);
      URL.revokeObjectURL(authUrl);
    }

    let signedLogoBase64 = "";
    if (logoForm) {
      const signedLogoUrl = await HttpService.getFileAdmin(
        "page-decorations",
        client.clientID.toString(),
        logoForm?.completedFormUrl,
      );

      const response = await fetch(signedLogoUrl);
      const blob = await response.blob();
      signedLogoBase64 = await convertBlobToBase64(blob);
      URL.revokeObjectURL(signedLogoUrl);
    }

    const questionsWithOptionsList = Array.from(
      questionsWithOptions,
      ([questionID, { questionText, options }]) => ({
        questionID,
        questionText,
        options,
      }),
    );

    const formData = {
      referenceDetail,
      order,
      client,
      referenceQuestionnaire,
      questionsWithOptions: questionsWithOptionsList,
      signedSignatureBase64,
      signedAuthBase64,
      signedLogoBase64,
      baseUrl: siteBaseUrl,
      byMail,
      diocese,
      diocese2,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceQuestionnaire/SendReferenceQuestionnaireLetter`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to send the email.");
      return false;
    }

    if (byMail) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ReferenceQuestionnaire.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } else {
      const result = await response.json();

      if (result.success) {
        console.log("Form successfully submitted and email sent.");
        return true;
      } else {
        console.error("Error from server:", result.message);
        return false;
      }
    }
  } catch (error) {
    console.error("Error while sending the email:", error);
    return false;
  }
};

export const updateBaseText = async (
  clientID: number,
  baseTextKey: keyof ReferenceQuestionnaire,
  baseText: string,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    clientID,
    baseTextKey,
    baseText,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceQuestionnaire/UpdateBaseText`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update the base text.");
    }
  } catch (error) {
    console.error("Error updating base text:", error);
    throw error;
  }
};

export const updateQuestion = async (
  questionID: number,
  text: string,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    questionID,
    text,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceQuestionnaire/UpdateQuestion`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update the question.");
    }
  } catch (error) {
    console.error("Error updating question:", error);
    throw error;
  }
};

export const updateOption = async (
  optionID: number,
  text: string,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    optionID,
    text,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceQuestionnaire/UpdateOption`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update the option.");
    }
  } catch (error) {
    console.error("Error updating option:", error);
    throw error;
  }
};

export const deleteBaseText = async (
  clientID: number,
  baseTextKey: keyof ReferenceQuestionnaire,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    clientID,
    baseTextKey,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceQuestionnaire/DeleteBaseText`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to delete the base text.");
    }
  } catch (error) {
    console.error("Error deleting base text:", error);
    throw error;
  }
};

export const deleteQuestion = async (questionID: number): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    questionID,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceQuestionnaire/DeleteQuestion`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to delete the question.");
    }
  } catch (error) {
    console.error("Error deleting question:", error);
    throw error;
  }
};

export const deleteOption = async (optionID: number): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    optionID,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceQuestionnaire/DeleteOption`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to delete the option.");
    }
  } catch (error) {
    console.error("Error deleting option:", error);
    throw error;
  }
};

export const createQuestionWithOptions = async (
  questionnaireID: number,
  questionText: string,
  options: string[],
): Promise<any> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    referenceQuestionnaireID: questionnaireID,
    questionText,
    options,
  };

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceQuestionnaire/CreateQuestionWithOptions`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(formData),
    },
  );

  if (!response.ok) {
    throw new Error("Error creating question");
  }

  return response.json();
};

export const saveFinishedReferenceQuestionnaire = async (
  clientID: number,
  order: SimpleOrder,
  referenceDetail: SimpleReferenceDetail,
  referenceFormAccess: ReferenceFormAccess,
  responses: { [key: string]: string },
  questionsWithOptions: Map<
    number,
    { questionText: string; options: QuestionOption[] }
  >,
  authorization: Authorization,
  comments: string,
  referenceQuestionnaire: ReferenceQuestionnaire,
  signatureForm: AdditionalForm | null,
  logoForm: AdditionalForm | null,
  diocese: string,
  diocese2: string,
  capacityComments?: string,
  noKnowledgeComments?: string,
  sendCopyEmail?: string,
): Promise<boolean> => {
  let signedSignatureBase64 = "";
  if (signatureForm) {
    const signedSignatureUrl = await HttpService.getFile(
      "signatures",
      clientID.toString(),
      signatureForm?.completedFormUrl,
    );

    const response = await fetch(signedSignatureUrl);
    const blob = await response.blob();
    signedSignatureBase64 = await convertBlobToBase64(blob);
  }

  let signedLogoBase64 = "";
  if (logoForm) {
    const signedLogoUrl = await HttpService.getFile(
      "page-decorations",
      clientID.toString(),
      logoForm?.completedFormUrl,
    );

    const response = await fetch(signedLogoUrl);
    const blob = await response.blob();
    signedLogoBase64 = await convertBlobToBase64(blob);
  }

  type ResponseItem = {
    kind: "question" | "option";
    id: number;
    value: string;
  };

  const responsesList: ResponseItem[] = Object.entries(responses).map(
    ([key, value]) => {
      const [prefix, idStr] = key.split("-");
      return {
        kind: prefix === "q" ? "question" : "option",
        id: Number(idStr),
        value,
      };
    },
  );

  const questionsList = Array.from(questionsWithOptions.entries()).map(
    ([key, value]) => ({
      questionID: key,
      questionText: value.questionText,
      options: value.options.map((option) => ({
        questionOptionID: option.questionOptionID,
        questionOptionText: option.questionOptionText,
      })),
    }),
  );

  try {
    const formData = {
      clientID,
      order,
      referenceDetail,
      referenceFormAccess,
      responses: responsesList,
      questionsWithOptions: questionsList,
      authorization,
      comments,
      referenceQuestionnaire,
      capacityComments,
      noKnowledgeComments,
      signedSignatureBase64,
      signedLogoBase64,
      diocese,
      diocese2,
      sendCopyEmail,
    };

    const response = await fetch(
      `${apiBaseUrl}/api/ReferenceQuestionnaire/SaveFinishedReferenceForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save the Reference Form.");
      return false;
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const getCleanSignature = async (
  signatureUrl: string,
): Promise<string> => {
  if (!signatureUrl.startsWith("blob:")) {
    throw new Error("Invalid signature URL: Expected a Blob URL.");
  }

  const base64Image = await convertBlobUrlToBase64(signatureUrl);

  const response = await fetch(
    `${apiBaseUrl}/api/ReferenceQuestionnaire/GetCleanSignature`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ base64Image }),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to clean signature");
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};
